import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTextInputComponent } from '../../../../../../editors/mat-text-input/mat-text-input.component';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import {
  FormArray,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { LocalPbxService } from '../../../../../local-pbx.service';
import { ButtonModel, LookupModel, PbxAccountButtonTemplateModel } from '../../../../../../api/models';
import { finalize, Subscription, take } from 'rxjs';
import { DeviceButtonSharedService } from '../../services/device-button-shared.service';
import { DeviceButtonComponent } from '../../device-button/device-button.component';
import { BUTTON_TYPES } from '../../constants/device-button-types';
import { NotificationService } from '../../../../../../notification/notification.service';
import {
  ConfirmDialogData,
  MatConfirmDialogComponent,
} from '../../../../../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSimpleSelectComponent } from '../../../../../../editors/mat-simple-select/mat-simple-select.component';
import { ButtonTemplateType } from '../../enums/button-template-type.enum';

export interface DeviceButtonTemplateEditDialogData {
  templateId: string;
  deviceButtons?: ButtonModel[];
  templateType?: ButtonTemplateType;
}

@Component({
  selector: 'app-device-button-template-edit-dialog',
  standalone: true,
  imports: [
    MatIconModule,
    MatDialogModule,
    FlexLayoutModule,
    MatTextInputComponent,
    MatButtonModule,
    Angular2PromiseButtonModule,
    FormsModule,
    ReactiveFormsModule,
    DeviceButtonComponent,
    MatProgressSpinner,
    MatSimpleSelectComponent,
  ],
  templateUrl: './device-button-template-edit-dialog.component.html',
  styleUrl: './device-button-template-edit-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceButtonTemplateEditDialogComponent implements OnInit, OnDestroy {
  public templateFormGroup: UntypedFormGroup;
  public serverError = '';

  public readonly templateTypes: LookupModel[] = [
    { id: ButtonTemplateType.Phone, text: 'Phone' },
    { id: ButtonTemplateType.Expansion, text: 'Expansion' },
  ];
  public readonly formSubmitted = signal<boolean>(false);
  public readonly templateLoaded = signal<boolean>(false);
  public readonly lookupButtonTypes: LookupModel[] = (BUTTON_TYPES || []).map(btn => ({
    id: btn.value,
    text: btn.title,
  }));

  private readonly _subscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly _data: DeviceButtonTemplateEditDialogData,
    private readonly _localPbxService: LocalPbxService,
    private readonly _fb: UntypedFormBuilder,
    private readonly _buttonSharedService: DeviceButtonSharedService,
    private readonly _notificationService: NotificationService,
    private readonly _dialog: MatDialog,
    private readonly _dialogRef: MatDialogRef<DeviceButtonTemplateEditDialogComponent>,
  ) {}

  get isFormValid(): boolean {
    return this.templateFormGroup?.valid;
  }

  get formButtonsArray(): UntypedFormGroup[] {
    const buttonsArray = this.templateFormGroup.get('buttons') as FormArray;
    return buttonsArray?.controls as UntypedFormGroup[];
  }
  get isTemplateNew(): boolean {
    return this._data.templateId === 'new';
  }

  public ngOnInit(): void {
    if (this.isTemplateNew) {
      this.createTemplateForm({ buttons: this._data.deviceButtons || [], type: this._data.templateType });
      this.templateLoaded.set(true);
    } else {
      this._subscription.add(this.subscribeToButtonTemplate());
    }
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public onTemplateDelete(): void {
    if (this.isTemplateNew) {
      return;
    }

    const dialogRef = this._dialog.open<MatConfirmDialogComponent, ConfirmDialogData, boolean>(
      MatConfirmDialogComponent,
      {
        data: {
          title: 'Delete Template',
          text: 'Are you sure you want to delete this template?',
          actionTitle: 'Delete',
          hideCancel: false,
          showCancelBtn: true,
        },
      },
    );

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteTemplate();
      }
    });
  }

  public onAddButtonTemplate(): void {
    (this.templateFormGroup.get('buttons') as FormArray)?.push(this._buttonSharedService.createButtonGroup(null, true));
  }

  public onTemplateSave(): void {
    if (!this.isFormValid) {
      return;
    }

    this.saveTemplate();
  }

  public onRemoveButton(index: number): void {
    const buttonsArray = this.templateFormGroup.get('buttons') as FormArray;

    buttonsArray.removeAt(index);

    buttonsArray.updateValueAndValidity();
  }

  private deleteTemplate(): void {
    this.formSubmitted.set(true);

    this._localPbxService
      .deletePbxAccountButtonTemplateById(this._data.templateId)
      .pipe(
        take(1),
        finalize(() => {
          this.formSubmitted.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this._dialogRef.close(true);
          this._notificationService.notifySuccess('Template deleted successfully');
        },
      });
  }

  private saveTemplate(): void {
    this.formSubmitted.set(true);

    const template = this.templateFormGroup.getRawValue() as PbxAccountButtonTemplateModel;

    this._localPbxService
      .savePbxAccountButtonTemplate(template)
      .pipe(
        take(1),
        finalize(() => {
          this.formSubmitted.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this._dialogRef.close(true);
          this._notificationService.notifySuccess('Template saved successfully');
        },
        error: (e: any) => {
          this.serverError = JSON.parse(e?.error)?.message || 'An error occurred while saving the template';
        },
      });
  }

  private createTemplateForm(template: Partial<PbxAccountButtonTemplateModel>): void {
    const { name, type, buttons } = template || {};

    this.templateFormGroup = this._fb.group({
      id: [this._data.templateId, [Validators.required]],
      name: [name || '', [Validators.required]],
      type: [type || '', [Validators.required]],
      buttons: this._fb.array(buttons?.map(button => this._buttonSharedService.createButtonGroup(button, true)) || []),
    });

    this.templateFormGroup.markAllAsTouched();
  }

  private subscribeToButtonTemplate(): Subscription {
    return this._localPbxService
      .getPbxAccountButtonTemplateById(this._data.templateId)
      .pipe(take(1))
      .subscribe(template => {
        if (template) {
          this.createTemplateForm(template);
          this.templateLoaded.set(true);
        }
      });
  }
}
